/**
 * Customer Auth helpers (kingspark-app) for web online ordering.
 */

import {
  User,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import {
  getCustomerAppAuth,
  getCustomerAppFunctions,
  isCustomerAppAuthConfigured,
} from './customerAppFirebase';

export { isCustomerAppAuthConfigured };

export function subscribeCustomerAppAuth(cb: (user: User | null) => void): () => void {
  if (!isCustomerAppAuthConfigured()) {
    cb(null);
    return () => undefined;
  }
  return onAuthStateChanged(getCustomerAppAuth(), cb);
}

export async function getCustomerAppIdToken(forceRefresh = false): Promise<string | null> {
  const user = getCustomerAppAuth().currentUser;
  if (!user) return null;
  return user.getIdToken(forceRefresh);
}

export async function signInCustomerApp(email: string, password: string): Promise<User> {
  const cred = await signInWithEmailAndPassword(getCustomerAppAuth(), email.trim(), password);
  await linkCustomerProfileBestEffort(cred.user);
  return cred.user;
}

export async function registerCustomerApp(params: {
  email: string;
  password: string;
  displayName: string;
  phone?: string;
}): Promise<User> {
  const cred = await createUserWithEmailAndPassword(
    getCustomerAppAuth(),
    params.email.trim(),
    params.password,
  );
  const name = params.displayName.trim();
  if (name) {
    await updateProfile(cred.user, { displayName: name });
  }
  await linkCustomerProfileBestEffort(cred.user, {
    email: params.email.trim(),
    displayName: name,
    phone: params.phone?.trim() || null,
  });
  return cred.user;
}

export async function resetCustomerAppPassword(email: string): Promise<void> {
  await sendPasswordResetEmail(getCustomerAppAuth(), email.trim());
}

export async function signOutCustomerApp(): Promise<void> {
  await signOut(getCustomerAppAuth());
}

async function linkCustomerProfileBestEffort(
  user: User,
  overrides?: { email?: string; displayName?: string; phone?: string | null },
): Promise<void> {
  try {
    const fn = httpsCallable(getCustomerAppFunctions(), 'linkPosCustomerProfile');
    await fn({
      email: overrides?.email ?? user.email ?? undefined,
      displayName: overrides?.displayName ?? user.displayName ?? undefined,
      phone: overrides?.phone !== undefined ? overrides.phone : undefined,
    });
  } catch (err) {
    // Profile link is best-effort; online order still attaches customer on POS via token.
    console.warn('linkPosCustomerProfile failed (non-fatal)', err);
  }
}
